import {useCallback, useEffect, useRef} from 'react';

const VALIDATION_SUCCESS_DEBOUNCE_MS = 400;
/** Pause after debounce so NVDA can finish echoing the last keystroke. */
const ANNOUNCE_SETTLE_MS = 600;
const SR_ONLY_STYLE =
  'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';

const SUCCESS_ALERT_ID = 'field-validation-success-alert';
const INVALID_ALERT_ID = 'field-validation-invalid-alert';

function clearValidationInvalidAlert() {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(INVALID_ALERT_ID)?.remove();
}

function clearValidationSuccessAlert() {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(SUCCESS_ALERT_ID)?.remove();
}

function announceValidationInvalid(text) {
  if (typeof document === 'undefined' || !text) {
    return;
  }

  clearValidationInvalidAlert();
  clearValidationSuccessAlert();

  const el = document.createElement('div');

  el.id = INVALID_ALERT_ID;
  el.setAttribute('role', 'alert');
  el.setAttribute('aria-live', 'assertive');
  el.setAttribute('aria-atomic', 'true');
  el.setAttribute('style', SR_ONLY_STYLE);
  el.textContent = text;
  document.body.appendChild(el);

  window.setTimeout(() => {
    el.remove();
  }, 2000);
}

function announceValidationSuccess(text) {
  if (typeof document === 'undefined' || !text) {
    return;
  }

  clearValidationSuccessAlert();
  clearValidationInvalidAlert();

  const el = document.createElement('div');

  el.id = SUCCESS_ALERT_ID;
  el.setAttribute('role', 'alert');
  el.setAttribute('aria-live', 'assertive');
  el.setAttribute('aria-atomic', 'true');
  el.setAttribute('style', SR_ONLY_STYLE);
  el.textContent = text;
  document.body.appendChild(el);

  window.setTimeout(() => {
    el.remove();
  }, 2000);
}

/**
 * On keydown: cancel any in-flight success, debounce 400ms, settle 600ms,
 * then announce if the field is still valid (~1s after last keystroke).
 */
export function useFieldValidationAnnouncement({
  enabled = true,
  errorMessage,
  formatMessage,
  invalid,
  label,
  value,
}) {
  const debounceRef = useRef(null);
  const announceRef = useRef(null);
  const errorMessageRef = useRef(errorMessage);
  const invalidRef = useRef(invalid);
  const valueRef = useRef(value);

  errorMessageRef.current = errorMessage;
  invalidRef.current = invalid;
  valueRef.current = value;

  useEffect(() => {
    if (invalid) {
      clearValidationSuccessAlert();
    } else {
      clearValidationInvalidAlert();
    }
  }, [invalid]);

  useEffect(() => {
    return () => {
      window.clearTimeout(debounceRef.current);
      window.clearTimeout(announceRef.current);
      clearValidationSuccessAlert();
      clearValidationInvalidAlert();
    };
  }, []);

  const handleKeyDown = useCallback(() => {
    if (!enabled) {
      return;
    }

    clearValidationSuccessAlert();
    clearValidationInvalidAlert();
    window.clearTimeout(debounceRef.current);
    window.clearTimeout(announceRef.current);

    debounceRef.current = window.setTimeout(() => {
      if (invalidRef.current) {
        const message = errorMessageRef.current;

        if (!message) {
          return;
        }

        announceRef.current = window.setTimeout(() => {
          if (invalidRef.current && errorMessageRef.current) {
            announceValidationInvalid(errorMessageRef.current);
          }
        }, ANNOUNCE_SETTLE_MS);

        return;
      }

      const trimmed = String(valueRef.current ?? '').trim();

      if (!trimmed || !label) {
        return;
      }

      const text = formatMessage(
        {
          id: 'field.validation.success',
          defaultMessage: '{label} is valid',
        },
        {label}
      );

      announceRef.current = window.setTimeout(() => {
        if (invalidRef.current) {
          return;
        }

        announceValidationSuccess(text);
      }, ANNOUNCE_SETTLE_MS);
    }, VALIDATION_SUCCESS_DEBOUNCE_MS);
  }, [enabled, formatMessage, label]);

  return {handleKeyDown};
}
